import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, ChevronDown, ChevronLeft, ChevronRight, ClipboardCheck, Pause, Play, Power, RefreshCw, Save, Radio } from "lucide-react";
import { useApi } from "../hooks/useApi";

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------
const fmtInt = (v) =>
  v == null || v === "" || Number.isNaN(Number(v))
    ? "–"
    : Math.round(Number(v)).toLocaleString("en-US");

const fmtNum = (v, dec = 2) =>
  v == null || v === "" || Number.isNaN(Number(v))
    ? "–"
    : Number(v).toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec });

const signColor = (v, dark) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return dark ? "text-slate-300" : "text-slate-600";
  return n > 0 ? (dark ? "text-emerald-400" : "text-emerald-600") : dark ? "text-rose-400" : "text-rose-600";
};

const sideColor = (dir, dark) =>
  dir === "BUY"
    ? dark ? "text-emerald-400" : "text-emerald-600"
    : dir === "SELL"
    ? dark ? "text-rose-400" : "text-rose-600"
    : "";

const fmtAge = (sec) => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  if (s < 90) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 90) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ${m % 60}m ago`;
};

// Countdown until the first PSE forecast is expected (period end + 15 min).
// Takes epoch seconds; returns "45 min" / "1h 25 min", or null once past due.
const fmtEta = (epochSec) => {
  if (epochSec == null || Number.isNaN(Number(epochSec))) return null;
  const remainingMs = Number(epochSec) * 1000 - Date.now();
  if (remainingMs <= 0) return null;
  const mins = Math.ceil(remainingMs / 60000);
  return mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60} min` : `${mins} min`;
};

// Warsaw-local calendar dates for the history quick-picks.
const warsawToday = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Warsaw" }).format(new Date());
const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

const KpiCard = ({ label, value, sub, accent, dark }) => (
  <div className={`rounded-xl border p-4 ${dark ? "bg-slate-800/60 border-slate-700" : "bg-white border-slate-200"}`}>
    <div className={`text-xs font-medium ${dark ? "text-slate-400" : "text-slate-500"}`}>{label}</div>
    <div className={`mt-1 text-2xl font-bold ${accent || (dark ? "text-slate-100" : "text-slate-800")}`}>{value}</div>
    {sub ? <div className={`mt-0.5 text-xs ${dark ? "text-slate-500" : "text-slate-400"}`}>{sub}</div> : null}
  </div>
);

// Legend for the Live Board cell colours, segregated by side
// (BUY = sk>0 shortage, SELL = sk<0 surplus). Each side shows its three states.
const CellLegend = () => {
  const sw = (cls) => <span className={`inline-block w-2.5 h-2.5 rounded-sm align-middle ${cls}`} />;
  const Row = ({ label, labelCls, muted, bright, noliq }) => (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
      <span className={`w-20 shrink-0 font-semibold ${labelCls}`}>{label}</span>
      <span className="inline-flex items-center gap-1">{sw(muted)} |SK|&gt;floor</span>
      <span className="inline-flex items-center gap-1">{sw(bright)} traded</span>
      <span className="inline-flex items-center gap-1">{sw(noliq)} no liquidity</span>
    </div>
  );
  return (
    <div className="text-[10px] normal-case font-normal text-slate-400 space-y-0.5">
      <Row label="BUY · SK+" labelCls="text-emerald-500" muted="bg-emerald-300" bright="bg-emerald-500" noliq="bg-cyan-500" />
      <Row label="SELL · SK−" labelCls="text-rose-500" muted="bg-rose-300" bright="bg-rose-500" noliq="bg-orange-500" />
    </div>
  );
};

const SectionCard = ({ title, icon, children, dark, right, collapsible = false, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  const expanded = !collapsible || open;
  return (
    <div className={`rounded-2xl border p-5 ${dark ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200 shadow-sm"}`}>
      <div className={`flex items-center justify-between ${expanded ? "mb-4" : ""}`}>
        <h2
          onClick={collapsible ? () => setOpen((v) => !v) : undefined}
          className={`flex items-center gap-2 text-sm font-semibold uppercase tracking-wide ${
            dark ? "text-slate-300" : "text-slate-600"
          } ${collapsible ? "cursor-pointer select-none" : ""}`}
          title={collapsible ? (open ? "collapse" : "expand") : undefined}
        >
          {icon}
          {title}
          {collapsible && (
            <ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"} ${dark ? "text-slate-500" : "text-slate-400"}`} />
          )}
        </h2>
        {right}
      </div>
      {expanded && children}
    </div>
  );
};

// v4 selection knobs — mirror the Backtesting v4 tab (SK floors + CEB-ladder
// margins), no edge model. The live trader re-reads these each cycle.
const CONFIG_FIELDS = [
  { key: "max_order_value_ph_eur", label: "Max order value — PH (€)", step: "1000", hint: "REMIT pre-trade ceiling per hourly order; 30k = never binds on 7-mo backtest incl. ~500 €/MWh regimes; enforced by the order gate (PowerBot limit mirrors it)" },
  { key: "max_order_value_qh_eur", label: "Max order value — QH (€)", step: "500", hint: "REMIT pre-trade ceiling per quarter-hourly order; 8k historical never-binds equivalent" },
  { key: "buy_floor", label: "BUY SK floor ≤60min lead (pred_sk ≥)", step: "10", hint: "MW; v5.1 ramp base (validated 500)" },
  { key: "buy_floor_mid", label: "BUY SK floor 60–120min lead", step: "10", hint: "MW; v5.1 ramp mid (validated 650)" },
  { key: "buy_floor_long", label: "BUY SK floor >120min lead", step: "10", hint: "MW; v5.1 ramp long (validated 800)" },
  { key: "sell_floor", label: "SELL SK floor (pred_sk ≤ −)", step: "10", hint: "MW; backtest-v4 default 100" },
  { key: "buy_margin", label: "BUY CEB margin (€/MWh)", step: "1", hint: "min required profit = −margin; caps fill prices at pred CEN − req (missing ladder: cap off)" },
  { key: "sell_margin", label: "SELL CEB margin — base (€/MWh)", step: "1", hint: "min required profit = −margin; caps fill prices at pred CEN + req (missing ladder: cap off)" },
  { key: "sell_margin_drop", label: "SELL margin @ pred DROP (€/MWh)", step: "1", hint: "applies when Δpred_sk < drop threshold since prev cycle (≤60min); empty = leg off; validated 0" },
  { key: "sell_margin_rise", label: "SELL margin @ pred RISE (€/MWh)", step: "1", hint: "applies when Δpred_sk > rise threshold; empty = leg off; validated −20" },
  { key: "sell_dpred_drop", label: "Δpred DROP threshold (MW)", step: "5", hint: "validated −50" },
  { key: "sell_dpred_rise", label: "Δpred RISE threshold (MW)", step: "5", hint: "validated +25" },
  { key: "anchor_tol", label: "Anchor tol (± €)", step: "1", hint: "band around last trade within which levels may be swept; saved live setting is also the Backtesting default" },
  { key: "lead_min_min", label: "Lead min (min to gate)", step: "5", hint: "operational buffer (live default 10)" },
  { key: "lead_max_min", label: "Lead max (min to gate)", step: "5", hint: "keep ≤ 300 — SK forecasts 5h" },
];

// ORDER LAYER (2026-09-04, Live Trading tab only): execution back-end + the hard
// pre-trade envelope enforced by order_gate.py OUTSIDE the model. Present in the
// config payload only for the trading schema; the tester never sees these keys.
const EXECUTION_MODES = [
  { value: "paper", label: "paper — no venue calls, paper fills are the record" },
  { value: "armed", label: "armed — gate runs, intents logged, nothing sent" },
  { value: "mirror", label: "mirror — REAL orders to venue, paper stays the record" },
  { value: "live", label: "live — REAL orders, exchange fills are the record" },
];
const EXEC_FIELDS = [
  { key: "max_contract_mw", label: "Max MW per contract", step: "1", hint: "gross filled + in-flight cap per contract (staging 5, prod 49)" },
  { key: "max_order_mw", label: "Max MW per order", step: "1", hint: "single IOC order size cap" },
  { key: "max_portfolio_mw", label: "Max portfolio MW", step: "10", hint: "sum of open gross positions over undelivered contracts" },
  { key: "max_orders_per_cycle", label: "Max orders / cycle", step: "1", hint: "hard count per decision cycle" },
  { key: "price_abs_min", label: "Price floor (€/MWh)", step: "10", hint: "absolute lower bound on any limit price" },
  { key: "price_abs_max", label: "Price ceiling (€/MWh)", step: "100", hint: "absolute upper bound on any limit price" },
  { key: "ramp_mw_cap", label: "Ramp cap MW (empty = off)", step: "1", hint: "100-10 rule: tighter per-contract cap during the first trading hours", nullable: true },
  { key: "max_order_actions_10s", label: "Order actions / 10 s", step: "5", hint: "venue quota 500/10s — keep ≤ 10 %" },
  { key: "max_order_actions_1h", label: "Order actions / 1 h", step: "50", hint: "venue quota 5000/1h — keep ≤ 10 %" },
  { key: "price_tick", label: "Venue price tick (€)", step: "0.01", hint: "prod Nord Pool PL 0.01; staging test exchange 0.1 — limits are rounded to it (BUY down, SELL up)" },
];
const EXEC_KEYS = ["execution_mode", ...EXEC_FIELDS.map((f) => f.key)];

// The three fundamental guards (mirror the Backtesting Guards block). Each is an
// enable flag + threshold column on live_config; the live trader enforces them
// per cycle (NULL fundamentals pass). skip_reason values: guard_subres /
// guard_reserve / guard_subzrb.
const GUARD_DEFS = [
  {
    onKey: "guard_buy_subres_on",
    valKey: "guard_buy_subres",
    label: "Block BUY on RES revision",
    cond: "sub_res >",
    step: "50",
    tip:
      "sub_res = sub_solar + sub_wind (MW): how much the freshest renewables forecast was revised UP vs the plan, " +
      "as of the decision. Positive = surplus risk → BUY (shortage) calls fail more often. Missing data passes.",
  },
  {
    onKey: "guard_buy_reserve_on",
    valKey: "guard_buy_reserve",
    label: "Block BUY on high reserve",
    cond: "bpkd_reserve >",
    step: "100",
    tip:
      "bpkd_reserve (MW): planned system reserve at the decision. Abundant reserve (> ~3500) = no shortage stress " +
      "→ weak BUYs. Missing data passes.",
  },
  {
    onKey: "guard_sell_subzrb_on",
    valKey: "guard_sell_subzrb",
    label: "Block SELL on collapsed zrb",
    cond: "sub_zrb <",
    step: "100",
    tip:
      "sub_zrb (MW): balancing-demand revision. Deeply negative = the collapse is already public/priced-in " +
      "→ weak SELLs. Missing data passes.",
  },
];

// Signed-value colour for ppro: green ≥ 0, amber in (−margin, 0), red < −margin.
const pproColor = (ppro, margin, dark) => {
  const v = Number(ppro);
  if (!Number.isFinite(v)) return dark ? "text-slate-500" : "text-slate-400";
  const m = Number(margin);
  if (v >= 0) return dark ? "text-emerald-400" : "text-emerald-600";
  if (Number.isFinite(m) && v < -m) return dark ? "text-rose-400" : "text-rose-600";
  return dark ? "text-amber-400" : "text-amber-600";
};

// Label for a cell with no VWAP. A NULL vwap does NOT mean missing book data —
// the trader only walks the book AFTER floors/guards pass, so on quiet days
// every below-floor row has no vwap. Label by skip_reason.
// Since 2026-07-31 the trader reports WHICH anchor-walk failure happened
// instead of one catch-all; "no book" now only appears on rows written before
// that (kept so history renders).
const noVwapLabel = (skip) => {
  if (skip === "empty_side") return "empty side";
  if (skip === "no_anchor") return "no anchor";
  if (skip === "outside_band") return "outside band";
  if (skip === "no_liquidity") return "no book";
  if (skip === "below_buy_floor" || skip === "below_sell_floor") return "below floor";
  if (skip && skip.startsWith("guard_")) return "guard";
  if (skip === "disabled") return "off";
  if (skip === "book_dirty") return "book syncing";
  if (skip === "out_of_window") return "out of window";
  if (skip === "position_full") return "position full";
  return "–";
};

// Floors/guards passed but the book yielded nothing — the "awaiting book"
// family that gets the cyan/orange cell background.
const NO_FILL_REASONS = new Set([
  "empty_side",
  "no_anchor",
  "outside_band",
  "no_liquidity", // legacy rows
  "book_dirty",
]);

// skip_reason → short badge label + colour classes (v4 vocabulary).
const SKIP_BADGES = {
  ceb_margin: {
    text: "CEN VETO",
    tip:
      "anchor-band liquidity existed but no level cleared the margin cap (pred CEN ∓ req). " +
      "The VWAP/ppro shown are the anchor-blend as monitoring context — NOT executed.",
  },
  // The anchor-walk failure family (ex-`no_liquidity`). All mean "floors and
  // guards passed, then the book gave us nothing" — but for different reasons,
  // and only empty_side is genuinely "no market".
  empty_side: {
    text: "EMPTY SIDE",
    tip: "no orders at all on the side we must hit (BUY signal → asks, SELL signal → bids)",
    cyan: true,
  },
  no_anchor: {
    text: "NO ANCHOR",
    tip:
      "the side we must hit HAS orders, but the contract has not printed a trade in the last 90 min, " +
      "so there is no last-trade price to centre the ±anchor_tol band on. A deep book still skips here.",
    cyan: true,
  },
  outside_band: {
    text: "OUTSIDE BAND",
    tip: "orders exist and we have an anchor, but every level is priced beyond last trade ±anchor_tol",
    cyan: true,
  },
  no_liquidity: {
    text: "NO BOOK",
    tip: "legacy catch-all (pre-2026-07-31): empty side, missing anchor or outside band — not distinguished",
    cyan: true,
  },
  guard_subres: { text: "GUARD RES", tip: "blocked by BUY-on-RES-revision guard (sub_res > threshold)" },
  guard_reserve: { text: "GUARD RESV", tip: "blocked by BUY-on-high-reserve guard (bpkd_reserve > threshold)" },
  guard_subzrb: { text: "GUARD ZRB", tip: "blocked by SELL-on-collapsed-zrb guard (sub_zrb < threshold)" },
  book_dirty: { text: "BOOK DIRTY", tip: "live book diverged, awaiting resync", amber: true },
  // Per-contract staleness vetoes (2026-08-05, post SIDC-break audit): fills
  // refused on frozen-but-clean books / hours-old anchors that the global
  // feed-age check cannot see during degraded venue states.
  book_stale: { text: "BOOK STALE", tip: "this contract's book unchanged > 10 min — frozen/degraded venue state; fills refused (LIVE_BOOK_STALE_S)", amber: true },
  anchor_stale: { text: "ANCHOR STALE", tip: "last trade > 60 min old — the ±tol collar is meaningless; fills refused (LIVE_ANCHOR_STALE_S)", amber: true },
};

// ---------------------------------------------------------------------------
// Shared by TWO tabs (2026-08-03): Live Testing (default props — the paper
// tester box, /api/live) and Live Trading (the live-trader box via
// apiBase="/live_trading" — see pages/LiveTrading.js). Identical endpoints and
// payload shapes; only the backing box/schema and labels differ.
const LiveTesting = ({
  darkMode: dark,
  apiBase = "/live",
  title = "Live Testing",
  subtitle = "Live paper-trading worker (v4 stack — SK floors + CEB-ladder margins) — scores every tradeable PL intraday contract each cycle.",
  serviceControl = false,
  serviceSuspend = false,
  ackControl = false,
  // Live Trading only: saving the config IS the material-change approval (one
  // action writes live_config AND the deployments registry); the standalone
  // header Approve button disappears and the enabled toggle is hidden — the
  // trader always trades per config, Kill/Suspend is the way to stop it.
  approveOnSave = false,
}) => {
  const { fetchGet, fetchPost, isAuthenticated, idToken } = useApi();
  // Audit POSTs (ack, kill/suspend/start) carry the ID token so the backend
  // can log a verified human identity (email) instead of the sub UUID.
  const auditHeaders = idToken ? { "X-Id-Token": idToken } : {};

  const [config, setConfig] = useState(null); // server copy
  const [form, setForm] = useState(null); // editable copy
  const [versions, setVersions] = useState(null); // selectable model serving versions
  const [board, setBoard] = useState(null);
  const [offset, setOffset] = useState(0); // cycles back from newest (0 = latest)
  const [loading, setLoading] = useState(false);
  const [histStart, setHistStart] = useState(() => warsawToday());
  const [histEnd, setHistEnd] = useState(() => warsawToday());
  const [history, setHistory] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Trader run-state (Live Trading only): desired vs agent-confirmed actual.
  const [svc, setSvc] = useState(null);
  const [svcBusy, setSvcBusy] = useState(false);
  // Acknowledge audit log (Live Trading only).
  const [acks, setAcks] = useState(null);
  const [ackBusy, setAckBusy] = useState(false);
  // Order layer (Live Trading only): 24 h counts + latest orders / fills / gate events.
  const [orders, setOrders] = useState(null);
  const hasExec = approveOnSave && config && "execution_mode" in config;

  const labelCls = `block text-xs font-medium mb-1 ${dark ? "text-slate-400" : "text-slate-500"}`;
  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm ${
    dark ? "bg-slate-800 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-800"
  } focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`;

  const loadConfig = useCallback(async () => {
    const res = await fetchGet(`${apiBase}/config`);
    const data = await res.json();
    setConfig(data.config);
    setForm((f) => f || { ...data.config });
    // Selectable serving versions (bundle registry). Best-effort: the pickers
    // just fall back to a free static option if this fails.
    try {
      const vres = await fetchGet(`${apiBase}/model_versions`);
      const vdata = await vres.json();
      if (vdata.status === "success") setVersions(vdata.versions);
    } catch {
      /* keep pickers usable with the current value only */
    }
  }, [fetchGet, apiBase]);

  const loadBoard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchGet(`${apiBase}/board`);
      const ct = res.headers.get("content-type") || "";
      if (!res.ok || !ct.includes("application/json")) {
        throw new Error(
          `/api${apiBase}/board returned ${res.status} ${ct.includes("html") ? "(HTML — the backend isn't redeployed with the live module yet)" : ""}`
        );
      }
      const data = await res.json();
      if (data.status !== "success") throw new Error(data.detail || "board failed");
      setBoard(data);
      setError(null);
    } catch (e) {
      setError(e.message || "Failed to load live board.");
    } finally {
      setLoading(false);
    }
  }, [fetchGet, apiBase]);

  const loadSvc = useCallback(async () => {
    if (!serviceControl) return;
    try {
      const res = await fetchGet(`${apiBase}/service`);
      const data = await res.json();
      if (data.status === "success") setSvc(data.service);
    } catch {
      /* keep the last known state; the poll retries */
    }
  }, [fetchGet, apiBase, serviceControl]);

  // Poll the run-state every 10 s — cheap, and a Kill from another browser
  // shows up here without a manual refresh.
  useEffect(() => {
    if (!isAuthenticated || !serviceControl) return undefined;
    loadSvc();
    const id = setInterval(loadSvc, 10000);
    return () => clearInterval(id);
  }, [isAuthenticated, serviceControl, loadSvc]);

  const loadAck = useCallback(async () => {
    if (!ackControl) return;
    try {
      const res = await fetchGet(`${apiBase}/ack`);
      const data = await res.json();
      if (data.status === "success") setAcks(data.acks);
    } catch {
      /* non-critical */
    }
  }, [fetchGet, apiBase, ackControl]);

  useEffect(() => {
    if (!isAuthenticated || !ackControl) return;
    loadAck();
  }, [isAuthenticated, ackControl, loadAck]);

  const loadOrders = useCallback(async () => {
    if (!approveOnSave) return;
    try {
      const res = await fetchGet(`${apiBase}/orders`);
      const data = await res.json();
      if (data.status === "success") setOrders(data);
    } catch {
      /* non-critical */
    }
  }, [fetchGet, apiBase, approveOnSave]);

  useEffect(() => {
    if (!isAuthenticated || !approveOnSave) return undefined;
    loadOrders();
    const id = setInterval(loadOrders, 30000);
    return () => clearInterval(id);
  }, [isAuthenticated, approveOnSave, loadOrders]);

  const doAck = async () => {
    setAckBusy(true);
    try {
      const res = await fetchPost(`${apiBase}/ack`, {}, { headers: auditHeaders });
      const data = await res.json();
      if (data.status !== "success") throw new Error(data.detail || "ack failed");
      setAcks(data.acks);
      setError(null);
    } catch (e) {
      setError(e.message || "acknowledge failed");
    } finally {
      setAckBusy(false);
    }
  };

  // REMIT approval registry: record a MATERIAL change (version flip, code
  // deploy, limit-envelope change, ramp step) — never nightly retrains.
  const doApprove = async () => {
    const change = window.prompt(
      "Approve a MATERIAL change — what changed?\n(e.g. \"version picker v5.1.0 -> v6.0.0\", \"serving code deploy git abc123\", \"ramp step 2 -> 10 MW\")");
    if (!change || !change.trim()) return;
    const reason = window.prompt("Why? (one line)") || "";
    setAckBusy(true);
    try {
      const res = await fetchPost(`${apiBase}/deployments`, { change, reason }, { headers: auditHeaders });
      const data = await res.json();
      if (data.status !== "success") throw new Error(data.detail || "approval failed");
      setError(null);
      window.alert(`Recorded: "${change}" — approval #${data.deployments?.[0]?.id}`);
    } catch (e) {
      setError(e.message || "approval failed");
    } finally {
      setAckBusy(false);
    }
  };

  const doService = async (action) => {
    const msgs = {
      kill:
        `KILL ${title}?\n\nThe serving process will be STOPPED and DISABLED - no predictions, no data pulls, no fills - until you press Start.` +
        (serviceSuspend ? "\n(Once the order API lands, Kill will ALSO cancel any open exchange orders.)" : ""),
      suspend:
        `Suspend ${title}?\n\nThe serving process will be STOPPED and DISABLED until you press Start.\n(Same as Kill for now; unlike Kill it will NOT cancel exchange orders in the future.)`,
    };
    if (msgs[action] && !window.confirm(msgs[action])) return;
    setSvcBusy(true);
    try {
      const res = await fetchPost(`${apiBase}/service`, { action }, { headers: auditHeaders });
      const data = await res.json();
      if (data.status !== "success") throw new Error(data.detail || "service request failed");
      setSvc(data.service);
      setError(null);
    } catch (e) {
      setError(e.message || "service request failed");
    } finally {
      setSvcBusy(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    loadConfig().catch(() => setError("Failed to load config."));
    loadBoard();
  }, [isAuthenticated, loadConfig, loadBoard]);

  const loadHistory = useCallback(async (start, end) => {
    setHistLoading(true);
    try {
      const res = await fetchGet(`${apiBase}/history?start=${start}&end=${end}`);
      const data = await res.json();
      if (data.status !== "success") throw new Error(data.detail || "history failed");
      setHistory(data);
    } catch (e) {
      setHistory(null);
      setError(e.message || "Failed to load history.");
    } finally {
      setHistLoading(false);
    }
  }, [fetchGet, apiBase]);

  // Load history on mount (default = today) and whenever the range changes.
  useEffect(() => {
    if (!isAuthenticated) return;
    loadHistory(histStart, histEnd);
  }, [isAuthenticated, histStart, histEnd, loadHistory]);

  // Auto-refresh the board every 60s — but pause while browsing history so the
  // window doesn't jump under the user (resumes when back at the latest).
  // The trade history refreshes on the same timer for the current range.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const id = setInterval(() => {
      if (offset === 0) loadBoard();
      loadHistory(histStart, histEnd);
    }, 60000);
    return () => clearInterval(id);
  }, [isAuthenticated, loadBoard, offset, loadHistory, histStart, histEnd]);

  const setRange = (start, end) => {
    setHistStart(start);
    setHistEnd(end);
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const saveConfig = async () => {
    // approveOnSave: describe the change from the actual field diff and ask
    // for a one-line reason BEFORE writing anything; Cancel aborts the save.
    let approvalChange = null;
    let approvalReason = "";
    if (approveOnSave) {
      const diffs = [];
      ["buy_floor", "sell_floor", "buy_margin", "sell_margin", "anchor_tol", "lead_min_min",
       "lead_max_min", "buy_floor_mid", "buy_floor_long", "buy_lead_split1", "buy_lead_split2",
       "sell_margin_drop", "sell_margin_rise", "sell_dpred_drop", "sell_dpred_rise",
       "guard_buy_subres_on", "guard_buy_subres", "guard_buy_reserve_on", "guard_buy_reserve",
       "guard_sell_subzrb_on", "guard_sell_subzrb", "sk_model_version", "ceb_model_version",
       "max_order_value_ph_eur", "max_order_value_qh_eur", ...(hasExec ? EXEC_KEYS : [])].forEach((k) => {
        if (String(config?.[k] ?? "") !== String(form[k] ?? "")) diffs.push(`${k} ${config?.[k]} -> ${form[k]}`);
      });
      if (hasExec && String(config?.execution_mode) !== String(form.execution_mode)
          && !window.confirm(`Switch execution_mode ${config?.execution_mode} -> ${form.execution_mode}?\n\n`
            + `mirror/live send REAL orders to the ${config?.venue_env} venue on the next cycle.`)) return;
      if (!diffs.length) return;
      approvalChange = `config: ${diffs.join("; ")}`;
      const r = window.prompt(`Approve this change?\n\n${diffs.join("\n")}\n\nReason (one line):`);
      if (r === null) return; // cancelled — nothing written
      approvalReason = r.trim();
    }
    setSaving(true);
    try {
      const res = await fetchPost(`${apiBase}/config`, {
        buy_floor: Number(form.buy_floor),
        sell_floor: Number(form.sell_floor),
        buy_margin: Number(form.buy_margin),
        sell_margin: Number(form.sell_margin),
        anchor_tol: Number(form.anchor_tol),
        lead_min_min: Number(form.lead_min_min),
        lead_max_min: Number(form.lead_max_min),
        enabled: approveOnSave ? true : !!form.enabled,
        guard_buy_subres_on: !!form.guard_buy_subres_on,
        guard_buy_subres: Number(form.guard_buy_subres),
        guard_buy_reserve_on: !!form.guard_buy_reserve_on,
        guard_buy_reserve: Number(form.guard_buy_reserve),
        guard_sell_subzrb_on: !!form.guard_sell_subzrb_on,
        guard_sell_subzrb: Number(form.guard_sell_subzrb),
        buy_floor_mid: Number(form.buy_floor_mid),
        buy_floor_long: Number(form.buy_floor_long),
        buy_lead_split1: Number(form.buy_lead_split1 ?? 60),
        buy_lead_split2: Number(form.buy_lead_split2 ?? 120),
        sell_margin_drop: form.sell_margin_drop === "" || form.sell_margin_drop == null ? null : Number(form.sell_margin_drop),
        sell_margin_rise: form.sell_margin_rise === "" || form.sell_margin_rise == null ? null : Number(form.sell_margin_rise),
        sell_dpred_drop: Number(form.sell_dpred_drop ?? -50),
        sell_dpred_rise: Number(form.sell_dpred_rise ?? 25),
        sk_model_version: form.sk_model_version,
        ceb_model_version: form.ceb_model_version,
        max_order_value_ph_eur: Number(form.max_order_value_ph_eur ?? 30000),
        max_order_value_qh_eur: Number(form.max_order_value_qh_eur ?? 8000),
        // order layer (trading schema only; the backend ignores absent keys)
        ...(hasExec ? {
          execution_mode: form.execution_mode,
          max_contract_mw: Number(form.max_contract_mw),
          max_order_mw: Number(form.max_order_mw),
          max_portfolio_mw: Number(form.max_portfolio_mw),
          max_orders_per_cycle: Number(form.max_orders_per_cycle),
          price_abs_min: Number(form.price_abs_min),
          price_abs_max: Number(form.price_abs_max),
          ramp_mw_cap: form.ramp_mw_cap === "" || form.ramp_mw_cap == null ? null : Number(form.ramp_mw_cap),
          ramp_mw_cap_set: true,
          max_order_actions_10s: Number(form.max_order_actions_10s),
          max_order_actions_1h: Number(form.max_order_actions_1h),
          price_tick: Number(form.price_tick ?? 0.01),
        } : {}),
      }, { headers: auditHeaders });
      const data = await res.json();
      if (data.status !== "success") throw new Error(data.detail || "save failed");
      setConfig(data.config);
      setForm({ ...data.config });
      setError(null);
      if (approveOnSave && approvalChange) {
        // config write succeeded -> record the approval (registry + Telegram)
        const ares = await fetchPost(`${apiBase}/deployments`,
          { change: approvalChange, reason: approvalReason }, { headers: auditHeaders });
        const adata = await ares.json();
        if (adata.status !== "success") throw new Error(adata.detail || "approval record failed");
      }
    } catch (e) {
      setError(e.message || "Failed to save config.");
    } finally {
      setSaving(false);
    }
  };

  const DIRTY_KEYS = [
    "buy_floor", "sell_floor", "buy_margin", "sell_margin", "anchor_tol", "lead_min_min", "lead_max_min", "enabled",
    "buy_floor_mid", "buy_floor_long", "sell_margin_drop", "sell_margin_rise", "sell_dpred_drop", "sell_dpred_rise",
    "guard_buy_subres_on", "guard_buy_subres", "guard_buy_reserve_on", "guard_buy_reserve",
    "guard_sell_subzrb_on", "guard_sell_subzrb",
    "sk_model_version", "ceb_model_version",
    "buy_lead_split1", "buy_lead_split2",
    "max_order_value_ph_eur", "max_order_value_qh_eur",
    ...EXEC_KEYS,
  ];
  const dirty = useMemo(() => {
    if (!config || !form) return false;
    return DIRTY_KEYS.some((k) => String(config[k] ?? "") !== String(form[k] ?? ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, form]);

  const cycles = board?.cycles || []; // newest-first
  // Show a 6-wide window that the user can slide back through history.
  const WINDOW = 6;
  const maxOffset = Math.max(0, cycles.length - WINDOW);
  const off = Math.min(offset, maxOffset);
  const canNewer = off > 0; // can slide toward the most recent
  const canOlder = off < maxOffset; // more history available
  // Window of the newest cycles starting `off` back, displayed oldest-left → newest-right.
  const displayCycles = [...cycles.slice(off, off + WINDOW)].reverse();
  const allRows = board?.rows || [];
  // Only "live" contracts: those with at least one prediction in the visible
  // window. Shifting the window left/right changes which contracts show.
  const rows = allRows.filter((r) => displayCycles.some((c) => r.cells[c.ts]));
  const status = board?.live_status || {};
  const skip = board?.skip_histogram || [];
  const hsum = history?.summary || {};
  const hrows = history?.rows || [];
  // Group history rows by delivery date so we can drop a per-day P&L subtotal
  // row whenever the selected range spans more than one delivery day.
  const histDayGroups = useMemo(() => {
    const groups = [];
    for (const r of hrows) {
      let g = groups[groups.length - 1];
      if (!g || g.date !== r.delivery_date) {
        g = { date: r.delivery_date, rows: [] };
        groups.push(g);
      }
      g.rows.push(r);
    }
    return groups;
  }, [hrows]);
  const histMultiDay = histDayGroups.length > 1;
  const daySummary = (dayRows) => {
    let pnl = 0;
    let mw = 0;
    let settledMwh = 0;
    let settled = false;
    dayRows.forEach((r) => {
      mw += Number(r.total_mw) || 0;
      if (r.pnl_eur != null) {
        settledMwh += Number(r.energy_mwh) || 0;
        pnl += Number(r.pnl_eur);
        settled = true;
      }
    });
    return {
      mw,
      pnl: settled ? pnl : null,
      eurPerMwh: settled && settledMwh ? pnl / settledMwh : null,
      positions: dayRows.length,
    };
  };
  // Left border that visually separates the table's column groups.
  const grpCls = dark ? "border-l border-slate-700" : "border-l border-slate-200";
  // The two-row grouped header, extracted so it can repeat per day group.
  // `repeated` adds a heavy top border to mark the start of a new day.
  const headerRows = (kp, repeated = false) => [
    <tr
      key={`${kp}-g`}
      className={`${repeated ? `border-t-4 ${dark ? "border-slate-700" : "border-slate-300"} ` : ""}${dark ? "text-slate-300" : "text-slate-500"} uppercase tracking-wide text-[10px]`}
    >
      <th className="py-1 px-2" colSpan={6} />
      <th className={`py-1 px-2 text-center ${grpCls}`} colSpan={4}>Order</th>
      <th className={`py-1 px-2 text-center ${grpCls}`} colSpan={2}>Model</th>
      <th className={`py-1 px-2 text-center ${grpCls}`} colSpan={2}>PSE Forecast</th>
      <th className={`py-1 px-2 text-center ${grpCls}`} colSpan={2}>PSE Settlement</th>
      <th className={`py-1 px-2 text-center ${grpCls}`} colSpan={2}>Result</th>
    </tr>,
    <tr
      key={`${kp}-c`}
      className={`${dark ? "text-slate-400" : "text-slate-500"} uppercase tracking-wide text-[10px]`}
    >
      <th className="text-left py-1.5 px-2">Product</th>
      <th className="text-left py-1.5 px-2">Delivery Date</th>
      <th className="text-left py-1.5 px-2">Delivery Time</th>
      <th className="text-left py-1.5 px-2">Decision</th>
      <th className="text-right py-1.5 px-2">To Gate</th>
      <th className="text-right py-1.5 px-2">RCE €</th>
      <th className={`text-right py-1.5 px-2 ${grpCls}`}>VWAP €</th>
      <th className="text-right py-1.5 px-2">Vol MW</th>
      <th className="text-right py-1.5 px-2">Fills</th>
      <th className="text-left py-1.5 px-2">Side</th>
      <th className={`text-right py-1.5 px-2 ${grpCls}`}>SK</th>
      <th className="text-right py-1.5 px-2">CEN €</th>
      <th className={`text-right py-1.5 px-2 ${grpCls}`}>SK</th>
      <th className="text-right py-1.5 px-2">CEN €</th>
      <th className={`text-right py-1.5 px-2 ${grpCls}`}>SK</th>
      <th className="text-right py-1.5 px-2">CEN €</th>
      <th className={`text-right py-1.5 px-2 ${grpCls}`}>P&L €</th>
      <th className="text-right py-1.5 px-2">€ / MWh</th>
    </tr>,
  ];
  const todayStr = warsawToday();
  const isRange = (s, e) => histStart === s && histEnd === e;
  const QUICK = [
    { label: "Tomorrow", s: addDays(todayStr, 1), e: addDays(todayStr, 1) },
    { label: "Today", s: todayStr, e: todayStr },
    { label: "Yesterday", s: addDays(todayStr, -1), e: addDays(todayStr, -1) },
    // Earliest traded day → tomorrow (covers near-future deliveries).
    { label: "All History", s: board?.min_trade_date || addDays(todayStr, -365), e: addDays(todayStr, 1) },
  ];
  const stale = Number(status.age_seconds) > 16 * 60;
  const skipMax = Math.max(1, ...skip.map((s) => Number(s.n) || 0));

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className={`text-2xl font-bold ${dark ? "text-slate-100" : "text-slate-800"}`}>{title}</h1>
          <p className={`text-sm ${dark ? "text-slate-400" : "text-slate-500"}`}>
            {subtitle}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {ackControl && (
            <div className="flex items-center gap-2">
              {acks?.[0] && (
                <span
                  className={`text-[11px] ${dark ? "text-slate-500" : "text-slate-400"}`}
                  title={`sk ${acks[0].sk_model_version} (${acks[0].sk_model || "no stamp"})\nceb ${acks[0].ceb_model_version} (${acks[0].ceb_model || "no stamp"})`}
                >
                  {/* never render a raw Cognito sub — the full value stays in
                      the DB; the directory usually resolves it to an email
                      before it gets here */}
                  last ack:{" "}
                  {/^[0-9a-f]{8}-[0-9a-f-]{20,}$/i.test(acks[0].acked_by || "")
                    ? "admin"
                    : acks[0].acked_by}{" "}
                  · {acks[0].ts_waw} (Warsaw)
                </span>
              )}
              {!approveOnSave && (
              <button
                onClick={doApprove}
                disabled={ackBusy}
                title="REMIT approval registry: record a MATERIAL change (version-channel flip, code deploy, limit-envelope change, ramp step). Nightly retrains are routine and never logged here."
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border ${
                  dark
                    ? "border-sky-500/60 text-sky-300 hover:bg-sky-500/10"
                    : "border-sky-400 text-sky-700 hover:bg-sky-50"
                } ${ackBusy ? "opacity-50" : ""}`}
              >
                <ClipboardCheck className="w-4 h-4" />
                Approve change
              </button>
              )}
              <button
                onClick={doAck}
                disabled={ackBusy}
                title="Record that you have reviewed the running setup — logs who/when + the serving model versions and bundle stamps"
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border ${
                  dark
                    ? "border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/10"
                    : "border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                } ${ackBusy ? "opacity-50" : ""}`}
              >
                <ClipboardCheck className="w-4 h-4" />
                {ackBusy ? "Saving…" : "Acknowledge"}
              </button>
            </div>
          )}
          <button
            onClick={loadBoard}
            disabled={loading}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border ${
              dark ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 text-slate-600 hover:bg-slate-50"
            }`}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-rose-300 bg-rose-50 text-rose-700 px-4 py-3 text-sm">{error}</div>
      ) : null}

      {/* ---------------- Trader run-state (Kill / Suspend / Start) ---------------- */}
      {serviceControl && (() => {
        const desired = svc?.desired;
        const settled = !!svc?.settled;
        const cls = !svc
          ? dark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
          : desired === "killed"
          ? dark ? "border-rose-500/50 bg-rose-500/10" : "border-rose-300 bg-rose-50"
          : desired === "suspended"
          ? dark ? "border-amber-500/50 bg-amber-500/10" : "border-amber-300 bg-amber-50"
          : dark ? "border-emerald-500/40 bg-emerald-500/5" : "border-emerald-300 bg-emerald-50";
        const labelTxt = !svc
          ? "…"
          : !settled
          ? desired === "running" ? "STARTING…" : "STOPPING…"
          : desired === "running" ? "RUNNING" : desired.toUpperCase();
        const labelCls2 = !svc
          ? dark ? "text-slate-400" : "text-slate-500"
          : desired === "killed"
          ? dark ? "text-rose-300" : "text-rose-700"
          : desired === "suspended"
          ? dark ? "text-amber-300" : "text-amber-700"
          : dark ? "text-emerald-300" : "text-emerald-700";
        const btn = (extra) =>
          `inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${extra} ${svcBusy ? "opacity-50 pointer-events-none" : ""}`;
        return (
          <div className={`rounded-2xl border p-4 flex items-center justify-between flex-wrap gap-3 ${cls}`}>
            <div className="flex items-center gap-3">
              <Power className={`w-5 h-5 ${labelCls2}`} />
              <div>
                <div className={`text-sm font-bold ${labelCls2}`}>TRADER {labelTxt}</div>
                <div className={`text-[11px] ${dark ? "text-slate-500" : "text-slate-500"}`}>
                  {svc
                    ? `${svc.requested_by ? `${desired === "running" ? "started" : desired} by ${svc.requested_by}` : "state"} · requested ${svc.requested_at_waw} (Warsaw)` +
                      (svc.actual_age_s != null ? ` · agent confirmed ${svc.actual_age_s}s ago` : " · agent not reporting")
                    : "loading run-state…"}
                  {!settled && svc ? " · waiting for the box agent…" : ""}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {desired === "running" ? (
                <>
                  {serviceSuspend && (
                    <button
                      type="button"
                      onClick={() => doService("suspend")}
                      className={btn(dark ? "border border-amber-500/60 text-amber-300 hover:bg-amber-500/10" : "border border-amber-400 text-amber-700 hover:bg-amber-100")}
                    >
                      <Pause className="w-4 h-4" />
                      Suspend
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => doService("kill")}
                    className={btn(dark ? "bg-rose-600 text-white hover:bg-rose-500" : "bg-rose-600 text-white hover:bg-rose-700")}
                  >
                    <Power className="w-4 h-4" />
                    Kill
                  </button>
                </>
              ) : svc ? (
                <button
                  type="button"
                  onClick={() => doService("start")}
                  className={btn(dark ? "bg-emerald-600 text-white hover:bg-emerald-500" : "bg-emerald-600 text-white hover:bg-emerald-700")}
                >
                  <Play className="w-4 h-4" />
                  Start
                </button>
              ) : null}
            </div>
          </div>
        );
      })()}

      {/* ---------------- Control panel ---------------- */}
      {form ? (
        <SectionCard
          title="Control Panel — Live Strategy"
          icon={<Activity className="w-4 h-4" />}
          dark={dark}
          collapsible
          defaultOpen={false}
          right={
            <span className={`text-xs ${dark ? "text-slate-500" : "text-slate-400"}`}>
              updated {config?.updated_at_waw} ({fmtAge(config?.updated_age_seconds)})
            </span>
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {CONFIG_FIELDS.map((f) => (
              <div key={f.key}>
                <label className={labelCls}>{f.label}</label>
                <input
                  type="number"
                  step={f.step}
                  className={inputCls}
                  value={form[f.key]}
                  onChange={(e) => setField(f.key, e.target.value === "" ? "" : Number(e.target.value))}
                />
                <div className={`mt-1 text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>{f.hint}</div>
              </div>
            ))}
          </div>

          {/* Guards — fundamental trade filters (mirror the Backtesting Guards block). */}
          <div className={`rounded-xl border p-4 mb-4 ${dark ? "bg-slate-800/40 border-slate-700" : "bg-slate-50 border-slate-200"}`}>
            <div className={`text-xs font-semibold uppercase tracking-wide mb-3 ${dark ? "text-slate-400" : "text-slate-500"}`}>
              Guards — fundamental trade filters
            </div>
            <div className="space-y-2.5">
              {GUARD_DEFS.map(({ onKey, valKey, label, cond, step, tip }) => (
                <div key={onKey} className="flex flex-wrap items-center gap-x-3 gap-y-1.5" title={tip}>
                  <label className={`flex items-center gap-2 w-64 cursor-pointer text-sm ${dark ? "text-slate-200" : "text-slate-700"}`}>
                    <input
                      type="checkbox"
                      checked={!!form[onKey]}
                      onChange={(e) => setField(onKey, e.target.checked)}
                      className="w-4 h-4 rounded accent-blue-600"
                    />
                    {label}
                  </label>
                  <span className={`w-32 font-mono text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>{cond}</span>
                  <input
                    type="number"
                    step={step}
                    disabled={!form[onKey]}
                    value={form[valKey]}
                    onChange={(e) => setField(valKey, e.target.value === "" ? "" : Number(e.target.value))}
                    className={`${inputCls} !w-28 disabled:opacity-40`}
                  />
                  <span className={`text-xs ${dark ? "text-slate-500" : "text-slate-400"}`}>MW</span>
                </div>
              ))}
            </div>
            <div className={`mt-3 text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
              Guards block trades where the SK model is least reliable (validated July 2026). Missing data passes.
            </div>
          </div>

          {/* ORDER LAYER — execution back-end + hard pre-trade envelope (trading tab only). */}
          {hasExec && (
            <div className={`rounded-xl border p-4 mb-4 ${dark ? "bg-slate-800/40 border-slate-700" : "bg-slate-50 border-slate-200"}`}>
              <div className={`text-xs font-semibold uppercase tracking-wide mb-3 ${dark ? "text-slate-400" : "text-slate-500"}`}>
                Execution — order layer
                <span className={`ml-2 normal-case font-normal ${dark ? "text-slate-500" : "text-slate-400"}`}>
                  venue: <span className={`font-mono ${config.venue_env === "prod" ? "text-rose-500" : "text-amber-500"}`}>{config.venue_env}</span>
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <label className={labelCls}>Execution mode</label>
                  <select
                    className={inputCls}
                    value={form.execution_mode || "paper"}
                    onChange={(e) => setField("execution_mode", e.target.value)}
                  >
                    {EXECUTION_MODES.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                  <div className={`mt-1 text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
                    Read every cycle; no restart. mirror/live place one IOC limit per contract at the walk cap.
                  </div>
                </div>
                {EXEC_FIELDS.map((f) => (
                  <div key={f.key}>
                    <label className={labelCls}>{f.label}</label>
                    <input
                      type="number"
                      step={f.step}
                      className={inputCls}
                      value={form[f.key] == null ? "" : form[f.key]}
                      onChange={(e) => setField(f.key, e.target.value === "" ? (f.nullable ? "" : "") : Number(e.target.value))}
                    />
                    <div className={`mt-1 text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>{f.hint}</div>
                  </div>
                ))}
              </div>
              <div className={`mt-3 text-[10px] ${dark ? "text-amber-500" : "text-amber-600"}`}>
                These are the REMIT pre-trade limits (order_gate.py). Every reject/reduction is logged in gate_events; changing them is a material change and lands in the approvals registry.
              </div>
            </div>
          )}

          {/* Model serving versions — which S3 channel (intraday-models/<kind>/<version>/)
              the live trader loads its newest bundle from. Changing one restarts the
              trader on its next cycle (atomic model+code reload). */}
          <div className={`rounded-xl border p-4 mb-4 ${dark ? "bg-slate-800/40 border-slate-700" : "bg-slate-50 border-slate-200"}`}>
            <div className={`text-xs font-semibold uppercase tracking-wide mb-3 ${dark ? "text-slate-400" : "text-slate-500"}`}>
              Model serving versions
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: "sk_model_version", label: "SK model version", list: versions?.sk },
                { key: "ceb_model_version", label: "CEB ladder version", list: versions?.ceb },
              ].map(({ key, label, list }) => {
                const opts = (list || []).map((v) => v.version);
                if (form[key] && !opts.includes(form[key])) opts.unshift(form[key]);
                const meta = (list || []).find((v) => v.version === form[key]);
                return (
                  <div key={key}>
                    <label className={labelCls}>{label}</label>
                    <select
                      className={inputCls}
                      value={form[key] || ""}
                      onChange={(e) => setField(key, e.target.value)}
                    >
                      {opts.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                    <div className={`mt-1 text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
                      {meta
                        ? `${meta.n_bundles} bundle${Number(meta.n_bundles) === 1 ? "" : "s"} · latest trained ${meta.latest_trained_waw} (data to ${meta.latest_data_through})`
                        : "serves the newest bundle under this version's S3 path"}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={`mt-3 text-[10px] ${dark ? "text-amber-500" : "text-amber-600"}`}>
              Changing a version restarts the live trader (atomic model + code reload) and it serves the newest
              bundle of that version. Only the version trained by the nightly keeps receiving fresh bundles.
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            {/* Live Trading (approveOnSave): no enabled toggle — the trader
                always trades per config; Kill/Suspend is the stop mechanism. */}
            {!approveOnSave && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-blue-600"
                  checked={!!form.enabled}
                  onChange={(e) => setField("enabled", e.target.checked)}
                />
                <span className={`text-sm font-medium ${form.enabled ? "text-emerald-600" : "text-rose-600"}`}>
                  {form.enabled ? "Strategy ENABLED" : "Strategy DISABLED"}
                </span>
              </label>
            )}
            <button
              onClick={saveConfig}
              disabled={saving || !dirty}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-sm font-medium disabled:opacity-50 ${
                approveOnSave ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {approveOnSave ? <ClipboardCheck className="w-4 h-4" /> : <Save className="w-4 h-4" />}
              {saving
                ? "Saving…"
                : dirty
                ? approveOnSave ? "Approve change (applies next cycle)" : "Save (applies next cycle)"
                : approveOnSave ? "No pending changes" : "Saved"}
            </button>
            <span className={`text-[11px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
              {approveOnSave
                ? "Approving records the change in the REMIT registry (who/what/why + Telegram) and re-tunes the running strategy next cycle."
                : "Writing these re-tunes the running strategy on its next cycle — no redeploy."}
            </span>
          </div>
        </SectionCard>
      ) : null}

      {/* ---------------- Status strip ---------------- */}
      {board ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          <div className={`rounded-xl border p-4 ${stale ? "border-rose-400 bg-rose-50/60" : dark ? "bg-slate-800/60 border-slate-700" : "bg-white border-slate-200"}`}>
            <div className={`flex items-center gap-1.5 text-xs font-medium ${stale ? "text-rose-600" : "text-emerald-600"}`}>
              <Radio className="w-3.5 h-3.5" /> {stale ? "STALE" : "LIVE"}
            </div>
            <div className={`mt-1 text-lg font-bold ${dark ? "text-slate-100" : "text-slate-800"}`}>{fmtAge(status.age_seconds)}</div>
            <div className={`text-[11px] ${dark ? "text-slate-500" : "text-slate-400"}`}>last cycle</div>
          </div>
          <div className={`rounded-xl border p-4 ${dark ? "bg-slate-800/60 border-slate-700" : "bg-white border-slate-200"}`}>
            <div className={`text-xs font-medium ${dark ? "text-slate-400" : "text-slate-500"}`}>Products scored</div>
            <div className={`mt-1 text-lg font-bold ${dark ? "text-slate-100" : "text-slate-800"}`}>{fmtInt(status.n_products)}</div>
          </div>
          <div className={`rounded-xl border p-4 ${dark ? "bg-slate-800/60 border-slate-700" : "bg-white border-slate-200"}`}>
            <div className={`text-xs font-medium ${dark ? "text-slate-400" : "text-slate-500"}`}>Would trade now</div>
            <div className={`mt-1 text-lg font-bold ${Number(status.n_would_trade) > 0 ? "text-emerald-600" : dark ? "text-slate-100" : "text-slate-800"}`}>
              {fmtInt(status.n_would_trade)}
            </div>
          </div>
          <div className={`rounded-xl border p-4 col-span-2 ${dark ? "bg-slate-800/60 border-slate-700" : "bg-white border-slate-200"}`}>
            <div className={`text-xs font-medium mb-1 ${dark ? "text-slate-400" : "text-slate-500"}`}>Skip reasons (latest cycle)</div>
            <div className="space-y-1">
              {skip.map((s) => (
                <div key={s.reason} className="flex items-center gap-2 text-[11px]">
                  <div className={`w-28 shrink-0 truncate ${s.reason === "would_trade" ? "text-emerald-600 font-medium" : dark ? "text-slate-400" : "text-slate-500"}`} title={s.reason}>
                    {s.reason}
                  </div>
                  <div className={`flex-1 h-3 rounded ${dark ? "bg-slate-800" : "bg-slate-100"}`}>
                    <div className={`h-3 rounded ${s.reason === "would_trade" ? "bg-emerald-500" : "bg-slate-400"}`} style={{ width: `${(Number(s.n) / skipMax) * 100}%` }} />
                  </div>
                  <div className={`w-7 text-right ${dark ? "text-slate-300" : "text-slate-600"}`}>{fmtInt(s.n)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {status.sk_model ? (
        <div className={`flex flex-wrap items-center gap-x-1.5 text-[11px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
          <span>Models — SK: {status.sk_model} · CEB:</span>
          {status.ceb_model === "ladder-live-failed" ? (
            <span
              className={`inline-flex items-center rounded px-1.5 py-0.5 font-medium ${dark ? "bg-amber-900/50 text-amber-300" : "bg-amber-100 text-amber-700"}`}
              title="the ladder inputs failed this cycle → ppro was NULL (rows still valid; margins passed). If this persists, investigate."
            >
              ladder-live-failed
            </span>
          ) : (
            <span>{status.ceb_model}</span>
          )}
        </div>
      ) : null}

      {/* ---------------- Order layer (Live Trading only) ---------------- */}
      {hasExec && orders ? (
        <SectionCard
          title={`Orders — ${config.execution_mode} @ ${config.venue_env}`}
          icon={<Activity className="w-4 h-4" />}
          dark={dark}
          collapsible
          defaultOpen
          right={
            <span className={`text-xs ${dark ? "text-slate-500" : "text-slate-400"}`}>
              24 h: {(orders.counts || []).length
                ? orders.counts.map((c) => `${c.state} ${c.n} (${Number(c.filled_mw).toFixed(1)}/${Number(c.qty_mw).toFixed(1)} MW)`).join(" · ")
                : "no orders"}
            </span>
          }
        >
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className={dark ? "text-slate-400" : "text-slate-500"}>
                  {["decision (Waw)", "contract", "side", "qty", "limit", "anchor", "pred CEN", "exp fill", "filled", "vwap", "state", "venue id / error"].map((h) => (
                    <th key={h} className="text-left px-2 py-1 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(orders.orders || []).map((o) => (
                  <tr key={o.id} className={`border-t ${dark ? "border-slate-800" : "border-slate-100"}`}>
                    <td className="px-2 py-1 font-mono whitespace-nowrap">{o.ts_waw}</td>
                    <td className="px-2 py-1 font-mono whitespace-nowrap">{o.name}</td>
                    <td className={`px-2 py-1 font-semibold ${o.side === "BUY" ? "text-emerald-500" : "text-rose-500"}`}>{o.side}</td>
                    <td className="px-2 py-1 text-right">{Number(o.qty_mw).toFixed(1)}</td>
                    <td className="px-2 py-1 text-right">{Number(o.limit_price).toFixed(2)}</td>
                    <td className="px-2 py-1 text-right">{o.anchor_px == null ? "—" : Number(o.anchor_px).toFixed(2)}</td>
                    <td className="px-2 py-1 text-right">{o.pred_cen == null ? "—" : Number(o.pred_cen).toFixed(1)}</td>
                    <td className="px-2 py-1 text-right">{o.expected_fill_mw == null ? "—" : Number(o.expected_fill_mw).toFixed(1)}</td>
                    <td className="px-2 py-1 text-right">{Number(o.filled_mw).toFixed(1)}</td>
                    <td className="px-2 py-1 text-right">{o.fill_vwap == null ? "—" : Number(o.fill_vwap).toFixed(2)}</td>
                    <td className={`px-2 py-1 font-mono ${
                      o.state === "filled" ? "text-emerald-500" : o.state === "partial" ? "text-amber-500"
                      : o.state === "rejected" || o.state === "error" ? "text-rose-500" : ""}`}>{o.state}</td>
                    <td className={`px-2 py-1 font-mono text-[10px] max-w-xs truncate ${dark ? "text-slate-500" : "text-slate-400"}`} title={o.error || o.venue_order_id || ""}>
                      {o.error || o.venue_order_id || "—"}
                    </td>
                  </tr>
                ))}
                {!(orders.orders || []).length && (
                  <tr><td colSpan={12} className={`px-2 py-3 text-center ${dark ? "text-slate-500" : "text-slate-400"}`}>
                    no orders yet — intents appear here once a contract crosses the floors in armed/mirror/live mode
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          {(orders.gate_events || []).length > 0 && (
            <div className={`mt-3 text-[11px] ${dark ? "text-slate-400" : "text-slate-500"}`}>
              <span className="font-semibold">Gate events (latest):</span>{" "}
              {orders.gate_events.slice(0, 8).map((g, i) => (
                <span key={i} className="font-mono mr-3">{g.ts_waw} {g.name} {g.side} {Number(g.qty_mw).toFixed(1)}@{Number(g.limit_price).toFixed(2)} → {g.outcome}:{g.rule}</span>
              ))}
            </div>
          )}
          {(orders.fills || []).length > 0 && (
            <div className={`mt-2 text-[11px] ${dark ? "text-slate-400" : "text-slate-500"}`}>
              <span className="font-semibold">Fills (latest):</span>{" "}
              {orders.fills.slice(0, 8).map((f, i) => (
                <span key={i} className="font-mono mr-3">{f.ts_waw} {f.name} {f.side} {Number(f.qty_mw).toFixed(1)} MW @ {Number(f.price).toFixed(2)}</span>
              ))}
            </div>
          )}
        </SectionCard>
      ) : null}

      {/* ---------------- Board matrix ---------------- */}
      {board ? (
        <SectionCard
          title="Live Board"
          icon={<Activity className="w-4 h-4" />}
          dark={dark}
          right={
            <div className="flex flex-col items-end gap-1">
              {off > 0 ? (
                <span className={`text-xs ${dark ? "text-amber-400" : "text-amber-600"}`}>
                  history · {off} cycle{off === 1 ? "" : "s"} back (live refresh paused)
                </span>
              ) : null}
              <CellLegend />
            </div>
          }
        >
          {rows.length === 0 ? (
            <div className={`text-sm ${dark ? "text-slate-400" : "text-slate-500"}`}>No predictions yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="app-table w-full text-xs border-collapse">
                <thead>
                  <tr className={`${dark ? "text-slate-400" : "text-slate-500"} uppercase tracking-wide text-[10px]`}>
                    <th className="text-left py-2 px-2 sticky left-0 bg-inherit">
                      <div className="flex items-center justify-between gap-2">
                        <span>Product</span>
                        <button
                          onClick={() => setOffset((o) => Math.min(maxOffset, o + 1))}
                          disabled={!canOlder}
                          title="Older cycles"
                          className={`inline-flex items-center justify-center rounded border p-0.5 disabled:opacity-25 ${
                            dark ? "border-slate-700 hover:bg-slate-800" : "border-slate-300 hover:bg-slate-100"
                          }`}
                        >
                          <ChevronLeft className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </th>
                    {displayCycles.map((c) => (
                      <th key={c.ts} className="text-right py-2 px-2 whitespace-nowrap">
                        <div className={`font-semibold ${dark ? "text-slate-300" : "text-slate-600"}`}>{c.label}</div>
                        <div className="font-normal normal-case">{c.day_label} · {fmtAge(c.age_seconds)}</div>
                      </th>
                    ))}
                    <th className="text-right py-2 px-2">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setOffset((o) => Math.max(0, o - 1))}
                          disabled={!canNewer}
                          title="Newer cycles"
                          className={`inline-flex items-center justify-center rounded border p-0.5 disabled:opacity-25 ${
                            dark ? "border-slate-700 hover:bg-slate-800" : "border-slate-300 hover:bg-slate-100"
                          }`}
                        >
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                        <span>Position</span>
                      </div>
                    </th>
                    <th className="text-right py-2 px-2">VWAP €</th>
                    <th className="text-right py-2 px-2">Money €</th>
                  </tr>
                </thead>
                <tbody className={dark ? "text-slate-200" : "text-slate-700"}>
                  {rows.map((r) => (
                    <tr key={r.contract_id} className={`border-t ${dark ? "border-slate-800" : "border-slate-100"}`}>
                      <td className={`py-1.5 px-2 align-top whitespace-nowrap ${r.kind === "PH" ? (dark ? "bg-slate-800" : "bg-slate-100") : ""}`}>
                        <div className="font-semibold">{r.product}</div>
                        <div className={`text-xs ${dark ? "text-slate-500" : "text-slate-400"}`}>{r.delivery_hm}</div>
                        <div className={`text-xs ${dark ? "text-slate-100" : "text-black"}`}>
                          RCE <span className="font-bold">{r.rce_eur != null ? `${fmtNum(r.rce_eur, 1)} €` : "–"}</span>
                        </div>
                      </td>
                      {displayCycles.map((c) => {
                        const cell = r.cells[c.ts];
                        if (!cell) return <td key={c.ts} className={`py-1.5 px-2 text-right align-top ${dark ? "text-slate-700" : "text-slate-300"}`}>·</td>;
                        const wt = cell.would_trade;
                        // Cell background, coloured by SK sign (green sk>0 = BUY,
                        // red sk<0 = SELL). v4 pass-floor check uses the CURRENT
                        // live_config per-side floor (BUY: sk ≥ buy_floor;
                        // SELL: sk ≤ −sell_floor), so it tracks the config you saved.
                        //  - trade filled           -> bright green / red
                        //  - no-fill family (empty side / no anchor / outside
                        //    band / dust / book dirty) -> cyan / orange
                        //  - passes side floor      -> muted green / red
                        const sk = Number(cell.pred_sk);
                        const pos = sk > 0;
                        const sideMargin = pos ? Number(config?.buy_margin) : Number(config?.sell_margin);
                        const traded = cell.trade_mw != null;
                        const blocked = NO_FILL_REASONS.has(cell.skip_reason);
                        const positionFull = cell.skip_reason === "position_full";
                        const badge = SKIP_BADGES[cell.skip_reason];
                        const passesFloor = pos
                          ? Number.isFinite(sk) && sk >= Number(config?.buy_floor)
                          : Number.isFinite(sk) && sk <= -Number(config?.sell_floor);
                        let bg = "";
                        if (traded) {
                          bg = pos ? (dark ? "bg-emerald-500/60" : "bg-emerald-300") : (dark ? "bg-rose-500/60" : "bg-rose-300");
                        } else if (blocked) {
                          bg = pos ? (dark ? "bg-cyan-700/50" : "bg-cyan-200") : (dark ? "bg-orange-700/50" : "bg-orange-200");
                        } else if (passesFloor) {
                          bg = pos ? (dark ? "bg-emerald-900/30" : "bg-emerald-50") : (dark ? "bg-rose-900/30" : "bg-rose-50");
                        }
                        return (
                          <td
                            key={c.ts}
                            className={`py-1.5 px-2 text-right align-top whitespace-nowrap ${bg}`}
                            title={cell.skip_reason || "would trade"}
                          >
                            {/* Primary: SK (pred_sk, MW), coloured by floor pass/fail. */}
                            <div className={`font-semibold ${passesFloor ? (pos ? (dark ? "text-emerald-400" : "text-emerald-600") : (dark ? "text-rose-400" : "text-rose-600")) : (dark ? "text-slate-400" : "text-slate-500")}`}>
                              SK {fmtInt(cell.pred_sk)}
                            </div>
                            {/* Secondary: CEN (EUR) + ppro (signed, margin-coloured). */}
                            <div className={dark ? "text-slate-400" : "text-slate-500"}>
                              CEN {fmtNum(cell.pred_cen, 0)}
                              {cell.ppro_mix != null ? (
                                <span className={`ml-1 font-medium ${pproColor(cell.ppro_mix, sideMargin, dark)}`}>
                                  ppro {fmtNum(cell.ppro_mix, 1)}
                                </span>
                              ) : (
                                <span className={`ml-1 ${dark ? "text-slate-500" : "text-slate-400"}`}>ppro –</span>
                              )}
                            </div>
                            <div className={`text-xs ${dark ? "text-slate-300" : "text-slate-600"}`}>
                              {cell.vwap != null
                                ? <>VWAP {fmtNum(cell.vwap, 1)} · {fmtNum(cell.fillable_mw, 1)} MW</>
                                : <span className={dark ? "text-slate-400" : "text-slate-500"}>{noVwapLabel(cell.skip_reason)}</span>}
                            </div>
                            <div className={`text-xs ${dark ? "text-slate-300" : "text-slate-600"}`}>
                              {fmtInt(cell.lead_min)}m to gate
                            </div>
                            {cell.trade_mw != null ? (
                              <div className={`mt-0.5 inline-block rounded text-white px-1 text-[10px] font-bold ${cell.side === "SELL" ? "bg-rose-600" : "bg-emerald-600"}`}>
                                {cell.side === "SELL" ? "▼" : "▲"} {cell.side} {fmtNum(cell.trade_mw, 1)} MW
                                {cell.trade_vwap != null ? ` @ ${fmtNum(cell.trade_vwap, 1)}` : ""}
                              </div>
                            ) : wt ? (
                              <div className="text-[10px] text-emerald-600 font-medium">would trade</div>
                            ) : positionFull ? (
                              <div className={`mt-0.5 inline-block rounded px-1 text-[10px] font-bold ${dark ? "bg-slate-600 text-slate-100" : "bg-slate-500 text-white"}`}>
                                POSITION FULL
                              </div>
                            ) : badge ? (
                              <div
                                className={`mt-0.5 inline-block rounded px-1 text-[10px] font-bold ${
                                  badge.amber
                                    ? dark ? "bg-amber-700 text-amber-100" : "bg-amber-500 text-white"
                                    : badge.cyan
                                    ? dark ? "bg-cyan-800 text-cyan-100" : "bg-cyan-600 text-white"
                                    : dark ? "bg-purple-700 text-purple-100" : "bg-purple-500 text-white"
                                }`}
                                title={badge.tip}
                              >
                                {badge.text}
                              </div>
                            ) : null}
                          </td>
                        );
                      })}
                      <td className={`py-1.5 px-2 text-right align-top font-semibold ${Number(r.position_mw) ? "" : dark ? "text-slate-600" : "text-slate-400"}`}>
                        {r.position_mw == null ? (
                          "–"
                        ) : (
                          <>
                            {Number(r.position_mw) !== 0 ? (
                              <span className={Number(r.position_mw) > 0 ? sideColor("BUY", dark) : sideColor("SELL", dark)}>
                                {Number(r.position_mw) > 0 ? "BUY" : "SELL"}{" "}
                              </span>
                            ) : null}
                            {fmtNum(Math.abs(Number(r.position_mw)), 1)} MW
                          </>
                        )}
                      </td>
                      <td className={`py-1.5 px-2 text-right align-top ${r.pos_vwap != null ? "" : dark ? "text-slate-600" : "text-slate-400"}`}>
                        {r.pos_vwap != null ? fmtNum(r.pos_vwap, 2) : "–"}
                      </td>
                      <td className={`py-1.5 px-2 text-right align-top ${dark ? "text-slate-300" : "text-slate-600"}`}>
                        {r.money_eur != null ? fmtInt(r.money_eur) : "–"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      ) : null}

      {/* ---------------- Live Trade History ---------------- */}
      <SectionCard
        title="Live Trade History"
        icon={<Activity className="w-4 h-4" />}
        dark={dark}
        right={
          histLoading ? (
            <span className={`text-xs ${dark ? "text-slate-500" : "text-slate-400"}`}>loading…</span>
          ) : null
        }
      >
        {/* Date range + quick picks */}
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <label className={labelCls}>Start Date</label>
            <input type="date" className={inputCls} value={histStart} onChange={(e) => setHistStart(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>End Date</label>
            <input type="date" className={inputCls} value={histEnd} onChange={(e) => setHistEnd(e.target.value)} />
          </div>
          <div className="flex flex-wrap gap-2">
            {QUICK.map((q) => (
              <button
                key={q.label}
                onClick={() => setRange(q.s, q.e)}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  isRange(q.s, q.e)
                    ? "bg-blue-600 text-white border-blue-600"
                    : dark
                    ? "border-slate-700 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {q.label}
              </button>
            ))}
          </div>
        </div>

        {/* Summary headline + cards */}
        <div className={`flex flex-wrap gap-x-8 gap-y-2 mb-4 text-sm ${dark ? "text-slate-300" : "text-slate-600"}`}>
          <div>
            <span className="font-semibold">{fmtInt(hsum.days)}</span> days ·{" "}
            <span className="text-emerald-600 font-semibold">{fmtInt(hsum.profitable_days)} profitable</span> /{" "}
            <span className="text-rose-600 font-semibold">{fmtInt(hsum.loss_days)} loss</span>
          </div>
          <div><span className="font-semibold">{fmtInt(hsum.trades)}</span> positions</div>
          <div>Avg ppro: <span className="font-semibold">{hsum.avg_ppro != null ? `${fmtNum(hsum.avg_ppro, 2)} €/MWh` : "–"}</span></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
          <KpiCard dark={dark} label="Total P&L" value={fmtInt(hsum.total_pnl_eur)} sub="EUR · settled" accent={signColor(hsum.total_pnl_eur, dark)} />
          <KpiCard dark={dark} label="EUR / MWh" value={hsum.eur_per_mwh != null ? fmtNum(hsum.eur_per_mwh, 2) : "–"} accent={signColor(hsum.eur_per_mwh, dark)} />
          <KpiCard dark={dark} label="Win Rate" value={hsum.win_pct != null ? `${fmtNum(hsum.win_pct, 1)}%` : "–"} />
          <KpiCard dark={dark} label="Positions" value={fmtInt(hsum.trades)} />
          <KpiCard dark={dark} label="Total Volume" value={fmtInt(hsum.total_mwh)} sub="MWh" />
          <KpiCard dark={dark} label="Avg MWh / Day" value={fmtInt(hsum.daily_avg_mwh)} sub="MWh" />
        </div>

        {/* Per-position table */}
        {hrows.length === 0 ? (
          <div className={`text-sm ${dark ? "text-slate-400" : "text-slate-500"}`}>
            No trades in this range{config?.buy_floor != null ? ` — none expected at BUY ≥ ${fmtInt(config.buy_floor)} / SELL ≤ −${fmtInt(config.sell_floor)}; lower the floors to produce trades.` : "."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="app-table w-full text-xs whitespace-nowrap">
              <thead>{headerRows("head")}</thead>
              <tbody className={dark ? "text-slate-200" : "text-slate-700"}>
                {histDayGroups.map((g, gi) => {
                  const ds = daySummary(g.rows);
                  const dash = <span className={dark ? "text-slate-600" : "text-slate-400"}>–</span>;
                  return (
                    <React.Fragment key={g.date}>
                      {gi > 0 ? headerRows(`hdr-${g.date}`, true) : null}
                      {g.rows.map((r, i) => {
                        // Colour Model SK by whether its sign agrees with the PSE
                        // Settlement SK (or the Forecast SK when settlement isn't
                        // published yet); no colour when neither reference exists.
                        const skRef = r.pse_sk_cost != null ? r.pse_sk_cost : r.pse_sk_fcst;
                        const skSignMatch =
                          r.model_sk != null &&
                          skRef != null &&
                          Math.sign(Number(r.model_sk)) === Math.sign(Number(skRef));
                        const skMatchCls =
                          r.model_sk == null || skRef == null
                            ? ""
                            : skSignMatch
                            ? dark
                              ? "text-emerald-400"
                              : "text-emerald-600"
                            : dark
                            ? "text-rose-400"
                            : "text-rose-600";
                        // Flag a bad entry: SK direction was right (signs agree) yet
                        // the position still lost money -> VWAP too high/low.
                        const vwapCls =
                          r.pnl_eur != null && Number(r.pnl_eur) < 0 && skSignMatch
                            ? dark
                              ? "text-rose-400 font-semibold"
                              : "text-rose-600 font-semibold"
                            : "";
                        // While unsettled, show how long until the first PSE
                        // forecast is expected ("in" + countdown across the two
                        // Result columns) instead of a bare "pending".
                        const eta = r.pnl_eur == null ? fmtEta(r.result_eta_epoch) : null;
                        return (
                        <tr key={`${g.date}-${i}`} className={`border-t transition-colors ${dark ? "border-slate-800 hover:bg-slate-800/60" : "border-slate-100 hover:bg-slate-50"}`}>
                          <td className={`py-1.5 px-2 font-semibold ${r.kind === "PH" ? (dark ? "bg-slate-800" : "bg-slate-100") : ""}`}>{r.product}</td>
                          <td className="py-1.5 px-2">{r.delivery_date}</td>
                          <td className="py-1.5 px-2">{r.delivery_hm ? r.delivery_hm.split(" ")[1] : ""}</td>
                          <td className={`py-1.5 px-2 ${dark ? "text-slate-400" : "text-slate-500"}`}>{r.decision_ts_waw || dash}</td>
                          <td className={`py-1.5 px-2 text-right ${dark ? "text-slate-400" : "text-slate-500"}`}>{r.lead_to_gate_min != null ? `${fmtInt(r.lead_to_gate_min)}m` : dash}</td>
                          <td className="py-1.5 px-2 text-right">{r.rce_eur != null ? fmtNum(r.rce_eur, 2) : dash}</td>
                          {/* Order */}
                          <td className={`py-1.5 px-2 text-right ${grpCls} ${vwapCls}`}>{fmtNum(r.vwap_eur, 2)}</td>
                          <td className="py-1.5 px-2 text-right">{fmtNum(r.total_mw, 1)}</td>
                          <td className="py-1.5 px-2 text-right">{fmtInt(r.n_fills)}</td>
                          <td className={`py-1.5 px-2 font-medium ${sideColor(r.side, dark)}`}>{r.side}</td>
                          {/* Model */}
                          <td className={`py-1.5 px-2 text-right font-semibold ${grpCls} ${skMatchCls}`}>{r.model_sk != null ? fmtInt(r.model_sk) : dash}</td>
                          <td className="py-1.5 px-2 text-right">{r.model_cen != null ? fmtNum(r.model_cen, 2) : dash}</td>
                          {/* PSE Forecast */}
                          <td className={`py-1.5 px-2 text-right ${grpCls}`}>{r.pse_sk_fcst != null ? fmtInt(r.pse_sk_fcst) : dash}</td>
                          <td className="py-1.5 px-2 text-right">{r.pse_cen_fcst_eur != null ? fmtNum(r.pse_cen_fcst_eur, 2) : dash}</td>
                          {/* PSE Settlement */}
                          <td className={`py-1.5 px-2 text-right ${grpCls}`}>{r.pse_sk_cost != null ? fmtInt(r.pse_sk_cost) : dash}</td>
                          <td className="py-1.5 px-2 text-right">{r.pse_cen_cost_eur != null ? fmtNum(r.pse_cen_cost_eur, 2) : dash}</td>
                          {/* Result */}
                          <td className={`py-1.5 px-2 text-right font-semibold ${grpCls} ${r.pnl_eur != null ? signColor(r.pnl_eur, dark) : ""}`}>
                            {r.pnl_eur != null ? fmtNum(r.pnl_eur, 1) : <span className="text-amber-600">{eta ? "in" : "pending"}</span>}
                          </td>
                          <td className={`py-1.5 px-2 text-right ${r.pnl_eur != null && Number(r.energy_mwh) ? signColor(r.pnl_eur, dark) : ""}`}>
                            {r.pnl_eur != null && Number(r.energy_mwh)
                              ? fmtNum(Number(r.pnl_eur) / Number(r.energy_mwh), 2)
                              : <span className="text-amber-600">{eta || "pending"}</span>}
                          </td>
                        </tr>
                        );
                      })}
                      {histMultiDay ? (
                        <tr className={`border-t-2 font-semibold ${dark ? "border-slate-700 bg-slate-800/60 text-slate-100" : "border-slate-300 bg-slate-50 text-slate-700"}`}>
                          <td className="py-1.5 px-2" colSpan={7}>
                            {g.date} — daily total · {fmtInt(ds.positions)} positions
                          </td>
                          <td className="py-1.5 px-2 text-right">{fmtNum(ds.mw, 1)}</td>
                          <td className="py-1.5 px-2" colSpan={8} />
                          <td className={`py-1.5 px-2 text-right ${grpCls} ${ds.pnl != null ? signColor(ds.pnl, dark) : ""}`}>
                            {ds.pnl != null ? fmtNum(ds.pnl, 1) : <span className="text-amber-600">pending</span>}
                          </td>
                          <td className={`py-1.5 px-2 text-right ${ds.eurPerMwh != null ? signColor(ds.eurPerMwh, dark) : ""}`}>
                            {ds.eurPerMwh != null ? fmtNum(ds.eurPerMwh, 2) : <span className="text-amber-600">pending</span>}
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default LiveTesting;
